/**
 * @file ProductTemplateStructure.tsx
 * @description Структура экрана «Страница товара» — шаблон из блоков
 * (phase19.16 Р2а; uiux-design-r2a §3, §6, §7; SB34, SB38, SB61): пояснение
 * «блоки применяются ко всем товарам, на сайт — с публикацией вида»,
 * замечания шаблона, Шапка · блоки шаблона с элементами под каждым ((з) у
 * обязательных; скрыть блок с обязательными нельзя) · Подвал, [+ Добавить
 * блок] (первой — группа «Страница товара»: раскладки), [Сбросить к
 * стандартному] и «Блоков N из 20».
 */

import { SITE_LIMITS } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";

import { Button } from "../../ui/button";
import { useBuilder } from "../builder-context";
import { AddBlockMenu } from "./AddBlockMenu";
import { AreaRow } from "./AreaRow";
import { BlockList } from "./BlockList";
import { TemplateElementLines } from "./TemplateElementLines";
import { TemplateIssues } from "./TemplateIssues";
import { useTemplateReset } from "../../../hooks/site-builder/useTemplateReset";
import { REQUIRED_BLOCK_HIDE_TEXT, TEMPLATE_BLOCKS_FULL_TEXT, TEMPLATE_DUPLICATE_HINT } from "../../../lib/site-builder/product-element-meta";
import { blockHasRequired } from "../../../lib/site-builder/template-rules";
import { cn } from "@/lib/utils";

const MAX = SITE_LIMITS.productTemplateSections;

export function ProductTemplateStructure() {
  const b = useBuilder();
  const { pages, selection } = b;
  const page = pages.page;
  const { reset } = useTemplateReset();
  const area = selection.sel.kind === "area" && !selection.panel ? selection.sel.area : null;
  const selectedBlock = selection.sel.kind === "block" && !selection.panel ? selection.sel.id : null;

  if (!page) return <p className="type-caption px-3 text-on-surface-variant">{pages.pageError ?? "Загружаем шаблон…"}</p>;
  const count = page.blocks.length;
  const full = count >= MAX;
  return (
    <div className="flex flex-col gap-1">
      <p className="type-caption px-1 pb-1 text-on-surface-variant">Блоки шаблона одни для всех товаров. Правки — в черновике: на сайте после «Опубликовать».</p>
      <TemplateIssues blocks={page.blocks} />
      <AreaRow area="header" selected={area === "header"} />
      <BlockList
        blocks={page.blocks}
        selectedId={selectedBlock}
        editable={b.canEditPages}
        onSelect={(block) => void b.select({ sel: { kind: "block", id: block.id }, panel: null })}
        renderBelow={(block) => <TemplateElementLines block={block} />}
        toggleLocked={(block) => (blockHasRequired(block) ? REQUIRED_BLOCK_HIDE_TEXT : null)}
        duplicateHint={TEMPLATE_DUPLICATE_HINT}
      />
      <AreaRow area="footer" selected={area === "footer"} />
      {b.canEditPages ? (
        <div className="flex flex-col gap-1 pt-2">
          <AddBlockMenu disabled={full} fullText={TEMPLATE_BLOCKS_FULL_TEXT} />
          {full ? <span className="type-caption px-1 text-on-surface-variant">{TEMPLATE_BLOCKS_FULL_TEXT}</span> : null}
          <Button variant="ghost" className="justify-start" onClick={() => void reset()}>
            <RotateCcw aria-hidden />
            Сбросить к стандартному
          </Button>
          <span className={cn("type-caption px-1", count >= MAX - 2 ? "text-warning" : "text-on-surface-variant")}>
            Блоков {count} из {MAX}
          </span>
        </div>
      ) : null}
    </div>
  );
}
